import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate, formatMass, formatNumber, titleCase } from '../../lib/format'
import type { ListResponse, ProductionLog } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select,
  Skeleton, StatusBadge, Textarea,
} from '../../components/ui'
import { CloseIcon, ChartIcon, PlusIcon } from '../../components/icons'
import { Wizard } from '../../components/Wizard'

const MINERALS = [
  'gold', 'copper', 'cobalt', 'manganese', 'emerald', 'amethyst',
  'aquamarine', 'tourmaline', 'limestone', 'granite', 'coal', 'other',
]

export default function Production() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [filter, setFilter] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['miner', 'production', filter],
    queryFn: () => api.get<ListResponse<ProductionLog>>(
      `/miner/production${filter ? `?mineral=${encodeURIComponent(filter)}` : ''}`,
    ),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.offline.post('/miner/production', body, 'Production log'),
    onSuccess: () => {
      // The dashboard surfaces the same rows, so refresh both.
      void qc.invalidateQueries({ queryKey: ['miner', 'production'] })
      void qc.invalidateQueries({ queryKey: ['miner', 'dashboard'] })
      setShowForm(false)
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    create.mutate({
      mineral_type: f.get('mineral_type'),
      volume_kg: Number(f.get('volume_kg')),
      period_start: f.get('period_start'),
      period_end: f.get('period_end'),
      buyer_name: f.get('buyer_name') || undefined,
      value_local: f.get('value_local') ? Number(f.get('value_local')) : undefined,
      workers_count: f.get('workers_count') ? Number(f.get('workers_count')) : undefined,
      notes: f.get('notes') || undefined,
    })
  }

  const items = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Production</h1>
          <p className="page-head__sub">Log what you extract and track its review status.</p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'Log production'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : 'Log production'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="New production log"
                      sub="Records go to your cooperative leader for review." />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack">
                {create.isError && (
                  <Alert tone="danger">{(create.error as Error).message}</Alert>
                )}
                {/* PWA: 3 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'What & how much', content: (
                      <div className="grid grid--2">
                        <Select label="Mineral" name="mineral_type" required defaultValue="">
                          <option value="" disabled>Select a mineral…</option>
                          {MINERALS.map((m) => <option key={m} value={m}>{titleCase(m)}</option>)}
                        </Select>
                        <Input label="Volume (kg)" name="volume_kg" type="number"
                               min="0" step="0.01" required placeholder="0.00" />
                      </div>
                    ) },
                    { title: 'Period', content: (
                      <div className="grid grid--2">
                        <Input label="Period start" name="period_start" type="date" required />
                        <Input label="Period end" name="period_end" type="date" required />
                      </div>
                    ) },
                    { title: 'Sale details', content: (
                      <div className="grid grid--2">
                        <Input label="Buyer (optional)" name="buyer_name" placeholder="Who purchased it" />
                        <Input label="Value in ZMW (optional)" name="value_local"
                               type="number" min="0" step="0.01" placeholder="0.00" />
                        <Input label="Workers involved (optional)" name="workers_count"
                               type="number" min="0" step="1" placeholder="0" />
                      </div>
                    ) },
                    { title: 'Notes & submit', content: (
                      <Textarea label="Notes (optional)" name="notes"
                                placeholder="Anything the reviewer should know" />
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={create.isPending}>Submit log</Button>
                      <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Your logs"
          sub={items.length ? `${items.length} record${items.length === 1 ? '' : 's'}` : undefined}
          action={
            <select className="select" style={{ width: 170, height: 34 }}
                    value={filter} onChange={(e) => setFilter(e.target.value)}
                    aria-label="Filter by mineral">
              <option value="">All minerals</option>
              {MINERALS.map((m) => <option key={m} value={m}>{titleCase(m)}</option>)}
            </select>
          }
        />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}>
              <Alert tone="danger">{(error as Error).message}</Alert>
            </div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack">
              <Skeleton h={18} /><Skeleton h={18} w="80%" /><Skeleton h={18} w="65%" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<ChartIcon size={22} />}
              title={filter ? `No ${titleCase(filter)} logs` : 'No production logged yet'}
              body={filter
                ? 'Try clearing the filter to see all of your records.'
                : 'Your logged output appears here once submitted.'}
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Mineral</th><th className="table__num">Volume</th>
                    <th>Period</th><th>Buyer</th>
                    <th className="table__num">Value (ZMW)</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p, i) => (
                    <tr key={p.id ?? i}>
                      <td>{titleCase(p.mineral_type)}</td>
                      <td className="table__num">{formatMass(p.volume_kg)}</td>
                      <td>
                        {formatDate(p.period_start)}
                        {p.period_end ? ` – ${formatDate(p.period_end)}` : ''}
                      </td>
                      <td>{p.buyer_name || '—'}</td>
                      <td className="table__num">{formatNumber(p.value_local, 2)}</td>
                      <td><StatusBadge status={p.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
